import jwtDecode from 'jwt-decode';
import { AuthIdToken } from './types';
import { externalLogout, isLogoutInProgress } from './utils';
import { store } from 'redux/store';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { queryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import { getGetTenantsQueryKey, Tenant } from 'api-client';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';

let navigateToHome: (() => void) | null = null;
let triggerTenantSelection: (() => void) | null = null;
let isTenantSwitchInProgress = false;
export const TENANT_SWITCH_NOTIFICATION_COOLDOWN_MS = 5000;

export const setNavigateToHome = (callback: () => void) => {
  navigateToHome = callback;
};

export const setTriggerTenantSelection = (callback: () => void) => {
  triggerTenantSelection = callback;
};

export interface JwtClaimVerificationResult {
  isValid: boolean;
  needsTenantRefresh: boolean;
  hasAccessToCurrentTenant: boolean;
  availableTenantIds: Tp3Id[];
  currentTenantId: Tp3Id | null;
  isWildcard: boolean;
}

export class JwtClaimVerificationService {
  /**
   * Extracts tenant entities from React Query cache data
   * The getTenants API returns Tenant[] directly, so we expect an array
   */
  private static extractTenantEntities(tenantData: unknown): Tenant[] {
    return Array.isArray(tenantData) ? tenantData : [];
  }

  private static getAllTenantIds(tenantEntities: Tenant[]): Tp3Id[] {
    return tenantEntities.map((t) => String(t.id)) as Tp3Id[];
  }

  static verifyJwtClaims(idToken: string): JwtClaimVerificationResult {
    try {
      const decodedToken = jwtDecode<AuthIdToken>(idToken);
      const jwtTenantIds = decodedToken.tp3_tenant_ids || '';
      const isWildcard = jwtTenantIds === '*';

      const { tenants: tenantState } = store.getState();
      const currentTenantId = tenantState.currentTenantPid;

      if (isWildcard) {
        return {
          isValid: true,
          needsTenantRefresh: true,
          hasAccessToCurrentTenant: currentTenantId !== null,
          availableTenantIds: [],
          currentTenantId,
          isWildcard: true,
        };
      }

      const availableTenantIds = jwtTenantIds
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      const hasAccessToCurrentTenant = currentTenantId
        ? availableTenantIds.includes(currentTenantId)
        : false;

      // Refresh tenants if user has available tenants OR has lost access to current tenant
      const needsTenantRefresh = availableTenantIds.length > 0 || !hasAccessToCurrentTenant;

      return {
        isValid: true,
        needsTenantRefresh,
        hasAccessToCurrentTenant,
        availableTenantIds,
        currentTenantId,
        isWildcard: false,
      };
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, 'Failed to verify JWT claims', error);
      return {
        isValid: false,
        needsTenantRefresh: false,
        hasAccessToCurrentTenant: false,
        availableTenantIds: [],
        currentTenantId: null,
        isWildcard: false,
      };
    }
  }

  static async handle403Error(): Promise<void> {
    try {
      if (isLogoutInProgress()) {
        return;
      }

      const idToken = localStorage.getItem('idToken');
      if (!idToken) {
        return;
      }

      const verification = this.verifyJwtClaims(idToken);

      if (!verification.isValid) {
        loggerService.log(
          LoggerLevel.DEBUG,
          '[LOGOUT_TRIGGER] Invalid JWT claims in handle403Error - triggering logout',
          {
            hasToken: !!idToken,
            currentTenantId: verification.currentTenantId,
            availableTenantIds: verification.availableTenantIds,
          }
        );
        await externalLogout({ skipLogoutWhenOffline: true });
        return;
      }

      await TenantRepository.refetchTenantsAndWait(queryClient);

      const tenantQueryKey = getGetTenantsQueryKey();
      const tenantData = queryClient.getQueryData(tenantQueryKey);

      const tenantEntities = this.extractTenantEntities(tenantData);

      if (tenantEntities.length === 0) {
        if (window.location.pathname !== '/no-account-found') {
          window.location.href = '/no-account-found';
        }
        return;
      }

      if (!verification.hasAccessToCurrentTenant) {
        const tenantIds = verification.isWildcard
          ? this.getAllTenantIds(tenantEntities)
          : verification.availableTenantIds;
        await this.switchToNextAvailableTenant(tenantEntities, tenantIds);
      }
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, 'Error handling 403', error);
      const idToken = localStorage.getItem('idToken');
      loggerService.log(
        LoggerLevel.DEBUG,
        '[LOGOUT_TRIGGER] Exception in handle403Error - triggering logout',
        {
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
          hasToken: !!idToken,
        }
      );
      await externalLogout({ skipLogoutWhenOffline: true });
    }
  }

  private static async switchToNextAvailableTenant(
    tenantEntities: Tenant[],
    availableTenantIds: Tp3Id[]
  ): Promise<void> {
    if (isTenantSwitchInProgress) {
      return;
    }

    isTenantSwitchInProgress = true;

    try {
      const { tenants: tenantState } = store.getState();
      const currentTenantId = tenantState.currentTenantPid;

      // Filter to only available tenants and find the next one after current
      const availableTenants = tenantEntities.filter((t) =>
        availableTenantIds.includes(String(t.id))
      );

      let nextTenantId: string | null = null;

      if (currentTenantId && availableTenants.length > 0) {
        const currentIndex = tenantEntities.findIndex((t) => t.id === currentTenantId);
        if (currentIndex >= 0) {
          // Current tenant found - rotate to find next available tenant
          const rotated = tenantEntities
            .slice(currentIndex + 1)
            .concat(tenantEntities.slice(0, currentIndex));
          const nextTenant = rotated.find((t) => availableTenantIds.includes(String(t.id)));
          nextTenantId = nextTenant?.id ?? availableTenants[0]?.id ?? null;
        } else {
          // Current tenant not found in entities – fall back to first available
          nextTenantId = availableTenants[0]?.id ?? null;
        }
      } else if (availableTenants.length > 0) {
        // No current tenant, use the first available
        nextTenantId = availableTenants[0]?.id || null;
      }

      if (nextTenantId) {
        // Check if there are multiple available tenants to show selection dialog
        const hasMultipleTenants = availableTenants.length > 1;

        TenantRepository.saveCurrent(nextTenantId);

        setTimeout(() => {
          if (navigateToHome) {
            navigateToHome();
          } else {
            window.location.href = '/';
          }

          // Trigger tenant selection dialog if multiple tenants are available
          if (hasMultipleTenants && triggerTenantSelection) {
            triggerTenantSelection();
          }
        }, 100);
      } else {
        if (window.location.pathname !== '/no-account-found') {
          window.location.href = '/no-account-found';
        }
      }
    } finally {
      isTenantSwitchInProgress = false;
    }
  }

  static async verifyAndHandleMismatches(): Promise<void> {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      return;
    }

    const verification = this.verifyJwtClaims(idToken);

    if (!verification.isValid) {
      loggerService.log(
        LoggerLevel.DEBUG,
        '[LOGOUT_TRIGGER] Invalid JWT claims in verifyAndHandleMismatches - triggering logout',
        {
          hasToken: !!idToken,
          currentTenantId: verification.currentTenantId,
          availableTenantIds: verification.availableTenantIds,
        }
      );
      await externalLogout({ skipLogoutWhenOffline: true });
      return;
    }

    if (verification.needsTenantRefresh) {
      await TenantRepository.refetchTenants(queryClient);
    }

    if (!verification.hasAccessToCurrentTenant) {
      if (!verification.isWildcard && verification.availableTenantIds.length === 0) {
        if (window.location.pathname !== '/no-account-found') {
          window.location.href = '/no-account-found';
        }
        return;
      }

      const tenantQueryKey = getGetTenantsQueryKey();
      let tenantData = queryClient.getQueryData(tenantQueryKey);

      let tenantEntities = this.extractTenantEntities(tenantData);

      if (tenantEntities.length === 0) {
        let retryCount = 0;
        const maxRetries = 3;

        while (tenantEntities.length === 0 && retryCount < maxRetries) {
          retryCount++;
          await TenantRepository.refetchTenantsAndWait(queryClient);
          tenantData = queryClient.getQueryData(tenantQueryKey);
          tenantEntities = this.extractTenantEntities(tenantData);

          if (tenantEntities.length === 0 && retryCount < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        if (tenantEntities.length === 0) {
          try {
            const { getTenants } = await import('api-client');
            const directTenantData = await getTenants();
            tenantEntities = this.extractTenantEntities(directTenantData);
          } catch (error) {
            loggerService.log(LoggerLevel.ERROR, 'Direct API call failed:', error);
          }
        }
      }

      if (tenantEntities.length > 0) {
        const tenantIds = verification.isWildcard
          ? this.getAllTenantIds(tenantEntities)
          : verification.availableTenantIds;
        await this.switchToNextAvailableTenant(tenantEntities, tenantIds);
      } else {
        loggerService.log(
          LoggerLevel.DEBUG,
          '[LOGOUT_TRIGGER] No tenant entities available after retries in verifyAndHandleMismatches - triggering logout',
          {
            hasToken: !!idToken,
            currentTenantId: verification.currentTenantId,
            availableTenantIds: verification.availableTenantIds,
          }
        );
        await externalLogout({ skipLogoutWhenOffline: true });
      }
    }
  }
}
